'use client';

import { FC, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import classNames from 'classnames';

import {
  Button,
  Close,
  Heading,
  IconSvg,
  NewAccordion,
  Placeholder,
  useData,
} from '@/components';
import { useEcomStoreSelector, useUiStoreSelector } from '@/data';
import { getAccountStickyOffset } from '@/utils';
import { useIsMobile, useSelectedStore } from '@/hooks';
import { EnumCustomerType, Order } from '@/types';

import { ReorderCarousel } from './ReorderCarousel';
import { getReorderItemIds, useReorderProducts } from './useReorderProducts';
import styles from './reorder.module.scss';

type NotificationState = 'changed' | 'differentInventory' | 'empty' | 'noStore';

const NOTIFICATIONS: Record<
  NotificationState,
  { title: string; subtext: string }
> = {
  changed: {
    title: 'Heads up — a few things have changed',
    subtext:
      "We're showing only the items from this order that are currently in stock. " +
      "Prices and deals reflect today's offers and may differ from your original purchase. " +
      'Your full original order details are below.',
  },
  differentInventory: {
    title: 'Heads up — a few things have changed',
    subtext:
      'You are currently using a different inventory than the one used for this order, ' +
      'so you might see differences in the products',
  },
  empty: {
    title: 'None of these items are in stock right now',
    subtext:
      'The products from this order are currently unavailable. Browse our latest menu to discover something new.',
  },
  noStore: {
    title: 'Choose a store to see availability',
    subtext:
      'Price, deals and stock depends on your store. Select a store to see what can you reorder from this order',
  },
};

interface INotificationCardProps {
  state: NotificationState;
  closable: boolean;
  onClose: () => void;
  onContinueShopping: () => void;
}

const NotificationCard: FC<INotificationCardProps> = ({
  state,
  closable,
  onClose,
  onContinueShopping,
}) => (
  <div className={styles.notification} role="status">
    {closable && (
      <Close className={styles.notification__close} onClick={onClose} />
    )}

    <p className={styles.notification__title}>{NOTIFICATIONS[state].title}</p>
    <p className={styles.notification__subtext}>
      {NOTIFICATIONS[state].subtext}
    </p>

    {state === 'empty' && (
      <Button
        ariaLabel="Continue shopping"
        className={styles.notification__cta}
        color="primary"
        onClick={onContinueShopping}
        size="sm"
      >
        Continue shopping
      </Button>
    )}
  </div>
);

interface ICurrentShoppingProps {
  location?: string | null;
  customerTypeLabel: string;
  shippingMethodLabel: string;
  shippingMethodIcon: string;
}

const CurrentShopping: FC<ICurrentShoppingProps> = ({
  location,
  customerTypeLabel,
  shippingMethodLabel,
  shippingMethodIcon,
}) => (
  <div className={styles.current_shopping}>
    <span className={styles.current_shopping__pin}>
      <IconSvg aria-hidden="true" name="outline-pin" />
    </span>

    <div className={styles.current_shopping__content}>
      <span className={styles.current_shopping__label}>
        Currently shopping at:
      </span>

      <div className={styles.current_shopping__info}>
        <span className={styles.current_shopping__location}>{location}</span>

        <div className={styles.current_shopping__badges}>
          <span className={styles.badge}>{customerTypeLabel}</span>

          <span className={classNames(styles.badge, styles.badge_method)}>
            <IconSvg aria-hidden="true" name={shippingMethodIcon} />
            {shippingMethodLabel}
          </span>
        </div>
      </div>
    </div>
  </div>
);

const CurrentShoppingSkeleton: FC = () => (
  <div className={styles.current_shopping}>
    <Placeholder className={styles.skeleton__pin} />

    <div className={styles.current_shopping__content}>
      <Placeholder className={styles.skeleton__label} />

      <div className={styles.current_shopping__info}>
        <Placeholder className={styles.skeleton__location} />

        <div className={styles.current_shopping__badges}>
          <Placeholder className={styles.skeleton__badge} />
          <Placeholder className={styles.skeleton__badge} />
        </div>
      </div>
    </div>
  </div>
);

interface IReorderProps {
  expandReorder?: boolean;
  order?: Order;
}

const Reorder: FC<IReorderProps> = ({ expandReorder = true, order }) => {
  const router = useRouter();
  const reorderRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useIsMobile(768);
  const { productCard } = useData();
  const { defaultSpaceBetween, isLargeCard } = productCard.displayProps;
  const { store } = useSelectedStore();
  const { delivery_details, customer_type } = useEcomStoreSelector([
    'delivery_details',
    'customer_type',
    'delivery_address',
  ]);
  const { setState: setUiState } = useUiStoreSelector([]);
  const { hasStore, isLoading, products } = useReorderProducts(
    getReorderItemIds(order),
  );

  const [isNotificationOpen, setIsNotificationOpen] = useState(true);

  const isPickup = !!delivery_details?.pickup;
  const customerTypeLabel =
    customer_type === EnumCustomerType.MEDICAL ? 'MEDICAL' : 'RECREATIONAL';
  const shippingMethodLabel = isPickup ? 'PICKUP' : 'DELIVERY';
  const shippingMethodIcon = isPickup ? 'store' : 'car';
  const currentLocation = store?.name;

  const orderStoreId =
    order?.treez_pay_ticket_ids_by_store?.[0]?.provider_store_id;

  const isDifferentInventory =
    (!!orderStoreId && orderStoreId !== store?.short_name) ||
    !!order?.delivery_details?.pickup !== !!delivery_details?.pickup ||
    !!order?.delivery_details?.delivery !== !!delivery_details?.delivery;

  const notification: NotificationState = !hasStore
    ? 'noStore'
    : !isLoading && products.length === 0
    ? 'empty'
    : isDifferentInventory
    ? 'differentInventory'
    : 'changed';

  const showNotification = !isLoading && isNotificationOpen;
  const showCarousel = hasStore && (isLoading || products.length > 0);

  const isNotificationClosable =
    notification === 'changed' || notification === 'differentInventory';

  useEffect(() => {
    if (!expandReorder || !isMobile) return;

    const timeoutId = window.setTimeout(() => {
      const el = reorderRef.current;
      if (!el) return;

      el.style.scrollMarginTop = `${getAccountStickyOffset() + 12}px`;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);

    return () => window.clearTimeout(timeoutId);
  }, [expandReorder, isMobile]);

  return (
    <div ref={reorderRef}>
      <NewAccordion
        className={styles.reorder}
        classNameHeader={styles.reorder__header}
        isExpanded={expandReorder}
        label={
          <Heading className={styles.reorder__title} level={3}>
            Reorder
          </Heading>
        }
      >
        <div className={styles.reorder__body}>
          {showNotification && (
            <NotificationCard
              closable={isNotificationClosable}
              onClose={() => setIsNotificationOpen(false)}
              onContinueShopping={() => router.push('/')}
              state={notification}
            />
          )}

          {notification === 'noStore' && (
            <Button
              ariaLabel="Choose your store"
              className={styles.notification__cta}
              color="primary"
              onClick={() => setUiState({ is_store_location_open: true })}
              size="sm"
            >
              Choose your store
            </Button>
          )}

          {showCarousel &&
            (isLoading ? (
              <CurrentShoppingSkeleton />
            ) : (
              <CurrentShopping
                customerTypeLabel={customerTypeLabel}
                location={currentLocation}
                shippingMethodIcon={shippingMethodIcon}
                shippingMethodLabel={shippingMethodLabel}
              />
            ))}

          {showCarousel && (
            <ReorderCarousel
              isLargeCard={isLargeCard}
              isLoading={isLoading}
              products={products}
              spaceBetween={defaultSpaceBetween}
            />
          )}
        </div>
      </NewAccordion>
    </div>
  );
};

export { Reorder };
